import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import { QRCodeSVG } from "qrcode.react";
import {
  selectGameObject,
  selectGameStatusObject,
} from "../gamestate/gameStateSlice";
import { selectAllPlayers } from "../player/playerSlice";
import PodiumGrid from "./PodiumGrid";
import { RocketLaunchOutlined } from "@mui/icons-material";
import { advanceState } from "../gamestate/GameState";
import { BrandMark } from "components";

// Define brand colors
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';
const CARD_BG = '#fff';

// Animation keyframes
const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 224, 102, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 224, 102, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 224, 102, 0);
  }
`;

const Root = styled('div')({
  label: "label-root",
  width: "100%",
  height: "100vh",
  position: "relative",
  overflow: "auto",
});

const HeaderSection = styled('div')({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "40px 0 30px",
});

const GameTitle = styled(Typography)({
  fontFamily: "'Gyparody', 'Bangers', 'Impact', sans-serif",
  fontWeight: 800,
  fontSize: '4rem',
  color: TEXT_COLOR,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 8,
  textShadow: '3px 3px 0px rgba(46, 196, 182, 0.5), 6px 6px 0px rgba(255, 224, 102, 0.3)',
  lineHeight: 1.1,
});

const ShareLink = styled('a')({
  fontSize: '1.3rem',
  color: TEXT_COLOR,
  fontWeight: 600,
  textDecoration: 'none',
  marginBottom: 24,
  padding: '8px 16px',
  background: `linear-gradient(120deg, ${ACCENT_YELLOW}30 0%, ${ACCENT_TEAL}30 100%)`,
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(44,62,80,0.15)',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(44,62,80,0.2)',
  }
});

const QRCodeContainer = styled(Paper)(({ theme }) => ({
  position: "fixed",
  left: theme.spacing(4),
  top: theme.spacing(4),
  backgroundColor: CARD_BG,
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  boxShadow: "0 4px 16px rgba(44,62,80,0.12)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const QRCodeLabel = styled(Typography)({
  color: TEXT_COLOR,
  fontWeight: "600",
  fontSize: '0.9rem',
});

const StartGameButton = styled(Button,
  { shouldForwardProp: (prop) => prop !== 'playerCount' })
  (({ theme, playerCount }) => ({
    background: `linear-gradient(120deg, ${ACCENT_YELLOW} 0%, ${ACCENT_TEAL} 50%, ${ACCENT_YELLOW} 100%)`,
    backgroundSize: '200% 200%',
    backgroundPosition: '0% 40%',
    color: TEXT_COLOR,
    padding: theme.spacing(1.5, 4),
    borderRadius: 12,
    fontWeight: 700,
    fontSize: '1.2em',
    textTransform: 'none',
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
    minWidth: 240,
    position: 'fixed',
    bottom: theme.spacing(6),
    left: '50%',
    transform: 'translateX(-50%)',
    transition: 'background-position 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0s',
    animation: playerCount >= 2 ? `${pulse} 3s infinite` : 'none',
    '&:hover': {
      backgroundPosition: '100% 60%',
      boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
    },
    '&.Mui-disabled': {
      background: '#e8e0c0',
      color: '#777570',
      boxShadow: 'none !important',
      cursor: 'not-allowed',
    }
  }));

const DialogPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  backgroundColor: CARD_BG,
  borderRadius: 16,
}));

const ConfirmButton = styled(Button)(({ theme }) => ({
  background: ACCENT_TEAL,
  color: TEXT_COLOR,
  fontWeight: 600,
  textTransform: 'none',
  padding: theme.spacing(1, 3),
  borderRadius: 8,
  '&:hover': {
    background: '#25a89c',
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  color: TEXT_COLOR,
  textTransform: 'none',
  fontWeight: 500,
}));

const Lobby = () => {
  const dispatch = useDispatch();
  const game = useSelector(selectGameObject);
  const gameState = useSelector(selectGameStatusObject);
  const players = useSelector(selectAllPlayers);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const joinUrl = `${window.location.origin}/game/${game.roomId}`;
  const playerCount = Object.keys(players).length;

  const handleOpenConfirmation = () => {
    setOpenConfirmation(true);
  };

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false);
  };

  const onStartGame = async () => {
    handleCloseConfirmation();
    await advanceState(game, players, dispatch);
  };

  let buttonCopy = "Start Game";
  if (gameState.round) {
    buttonCopy = "Begin Next Round";
  }
  if (playerCount === 0) {
    buttonCopy = "Waiting for Players...";
  }

  return (
    <Root>
      <HeaderSection>
        <BrandMark size="lg" sx={{ marginBottom: "16px" }} />
        <GameTitle variant="h1">Welcome to GROUPARDY!</GameTitle>
        <ShareLink
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Box component="span" sx={{ mr: 1 }}>🔗</Box>
          Share this link: {joinUrl}
        </ShareLink>
      </HeaderSection>

      <PodiumGrid />

      <QRCodeContainer elevation={3}>
        <QRCodeSVG value={joinUrl} size={150} />
        <QRCodeLabel variant="body2">
          Room: {game.roomId}
        </QRCodeLabel>
      </QRCodeContainer>

      <StartGameButton
        variant="contained"
        playerCount={playerCount}
        onClick={handleOpenConfirmation}
        startIcon={<RocketLaunchOutlined />}
        disabled={playerCount === 0}
      >
        {buttonCopy}
      </StartGameButton>

      <Dialog
        open={openConfirmation}
        onClose={handleCloseConfirmation}
        PaperComponent={DialogPaper}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem', color: TEXT_COLOR }}>
          Start the Game?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ color: TEXT_COLOR, mb: 2 }}>
            Once the game starts, no new players can join. Are you ready to begin?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', padding: '16px' }}>
          <CancelButton onClick={handleCloseConfirmation}>
            Cancel
          </CancelButton>
          <ConfirmButton variant="contained" onClick={onStartGame}>
            Yes, Start!
          </ConfirmButton>
        </DialogActions>
      </Dialog>
    </Root>
  );
};

export default Lobby;
